import { useMemo } from 'react';
import type { ForceGraphData } from '../../components/c3/correlationGraph/buildCombinedGraph';
import type {
  BlastRadiusScoreDocument,
  EntityRiskScoreDocument,
  SecurityEventItem,
  ThreatElementDocument,
  ThreatEventItem,
} from '../domain/types';
import { buildWorkspaceGraph } from '../selectors/workspace';
import { combine, useLogFailure } from './combine';
import { useDataClock } from './DataProvider';
import {
  useBlastRadiusScores,
  useBusinessElements,
  useEntityRiskScores,
  useInfrastructureElements,
  useSecurityEvents,
  useThreatElements,
  useThreatEvents,
} from './hooks';

export const EMPTY_GRAPH: ForceGraphData = {
  nodes: [],
  links: [],
  stats: { elementCount: 0, nodeCount: 0, linkCount: 0, crossLayerLinkCount: 0 },
};

const NONE_ELEMENTS: ThreatElementDocument[] = [];
const NONE_EVENTS: ThreatEventItem[] = [];
const NONE_SECURITY: SecurityEventItem[] = [];

export interface WorkspaceGraphData {
  graph: ForceGraphData;
  threatElements: ThreatElementDocument[];
  threatEvents: ThreatEventItem[];
  securityEvents: SecurityEventItem[];
  /** Nothing to draw yet: the first read, or a read again after a failure. */
  loading: boolean;
  /** A read failed. The graph and lists are empty, as the Workspace has always done on a failed read. */
  error: boolean;
  /** A read is under way, the first or a tick's. */
  fetching: boolean;
  refresh: () => Promise<void>;
}

/** The entity graph and the records behind it, from the shared cache. */
export function useWorkspaceGraphData(): WorkspaceGraphData {
  const { refreshNow } = useDataClock();
  const infrastructure = useInfrastructureElements();
  const business = useBusinessElements();
  const threatElements = useThreatElements();
  const threatEvents = useThreatEvents();
  const securityEvents = useSecurityEvents();
  const state = combine([infrastructure, business, threatElements, threatEvents, securityEvents]);
  const usable = state.ready && !state.failed;
  useLogFailure('Entity graph could not be loaded', [infrastructure, business, threatElements, threatEvents, securityEvents]);

  const graph = useMemo(
    () =>
      usable
        ? buildWorkspaceGraph({
            infrastructure: infrastructure.data ?? [],
            business: business.data ?? [],
            threatElements: threatElements.data ?? [],
            threatEvents: threatEvents.data ?? [],
          })
        : EMPTY_GRAPH,
    [usable, infrastructure.data, business.data, threatElements.data, threatEvents.data]
  );

  return {
    graph,
    threatElements: usable ? threatElements.data ?? NONE_ELEMENTS : NONE_ELEMENTS,
    threatEvents: usable ? threatEvents.data ?? NONE_EVENTS : NONE_EVENTS,
    securityEvents: usable ? securityEvents.data ?? NONE_SECURITY : NONE_SECURITY,
    loading: state.loading,
    error: state.error,
    fetching: state.fetching,
    refresh: refreshNow,
  };
}

export interface ScoresData<T> {
  rows: T[];
  loading: boolean;
  error: boolean;
  refresh: () => Promise<void>;
}

const NONE_ERI: EntityRiskScoreDocument[] = [];
const NONE_EBR: BlastRadiusScoreDocument[] = [];

/** The Entity Risk Index rows. Empty on a failed read, as before. */
export function useEntityRiskData(): ScoresData<EntityRiskScoreDocument> {
  const { refreshNow } = useDataClock();
  const scores = useEntityRiskScores();
  const state = combine([scores]);
  useLogFailure('Entity risk scores could not be loaded', [scores]);
  return {
    rows: state.failed ? NONE_ERI : scores.data ?? NONE_ERI,
    loading: state.loading,
    error: state.error,
    refresh: refreshNow,
  };
}

/** The Effective Blast Radius rows. Empty on a failed read, as before. */
export function useBlastRiskData(): ScoresData<BlastRadiusScoreDocument> {
  const { refreshNow } = useDataClock();
  const scores = useBlastRadiusScores();
  const state = combine([scores]);
  useLogFailure('Blast radius scores could not be loaded', [scores]);
  return {
    rows: state.failed ? NONE_EBR : scores.data ?? NONE_EBR,
    loading: state.loading,
    error: state.error,
    refresh: refreshNow,
  };
}
